<template>
    <div class="login-container">
        <div class="logo-section">
            <img 
                src="@/assets/action-logo.svg" 
                alt="Action Network Logo" 
                class="main-logo"
            >
        </div>
        <h1 class="welcome-text">Welcome to Action Network</h1>
        <h2 class="members-text">Members Area</h2>

        <!-- Email/Password Login Form -->
        <form @submit.prevent="loginWithEmail" class="login-form" v-if="showEmailLogin && !showForgotPassword">
            <div class="form-group">
                <label>Email</label>
                <input 
                    type="email" 
                    v-model="email" 
                    required 
                    placeholder="Enter your email"
                >
            </div>
            <div class="form-group">
                <label>Password</label>
                <div class="password-input-wrapper">
                    <input
                        :type="showPassword ? 'text' : 'password'"
                        v-model="password"
                        required
                        placeholder="Enter your password"
                        class="password-input"
                    >
                    <button
                        type="button"
                        @click="showPassword = !showPassword"
                        class="password-toggle"
                        :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    >
                        <span v-if="showPassword">👁️</span>
                        <span v-else>👁️‍🗨️</span>
                    </button>
                </div>
            </div>
            <button type="submit" class="login-btn">Login</button>
            <p class="forgot-password-text">
                <a href="#" @click.prevent="toggleForgotPassword">Forgot Password?</a>
            </p>
        </form>

        <!-- Forgot Password Form -->
        <form @submit.prevent="forgotPassword" class="login-form" v-if="showForgotPassword">
            <h3>Reset Password</h3>
            <div class="form-group">
                <label>Email</label>
                <input 
                    type="email" 
                    v-model="email" 
                    required 
                    placeholder="Enter your email"
                >
            </div>
            <button type="submit" class="login-btn">Send Reset Link</button>
            <p class="login-text">
                <a href="#" @click.prevent="toggleForgotPassword">Back to Login</a>
            </p>
        </form>

        <div v-if="error" class="error-container">
            <p class="error-message">{{ error }}</p>
        </div>

        <div v-if="success" class="success-container">
            <p class="success-message">{{ success }}</p>
        </div>
    </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';

// Native authentication services
import nativeAuthService from '@/services/nativeAuthService';
import { passwordResetService } from '@/services/passwordResetService';

export default {
    name: 'UserLogin',
    setup() {
        const router = useRouter();
        const error = ref('');
        const success = ref('');
        const email = ref('');
        const password = ref('');
        const showEmailLogin = ref(true);
        const showForgotPassword = ref(false);
        const showPassword = ref(false);
        

        const forgotPassword = async () => {
            try {
                error.value = '';
                success.value = '';
                
                if (!email.value) {
                    error.value = 'Please enter your email address';
                    return;
                }
                
                // Use native auth service
                const result = await passwordResetService.requestReset(email.value);
                if (result.success) {
                    success.value = 'Password reset email sent. Please check your inbox.';
                } else {
                    error.value = result.error || 'Failed to send reset email. Please verify your email address.';
                }
            } catch (err) {
                console.error('Password reset error:', err);
                error.value = 'Failed to send reset email. Please verify your email address.';
            }
        };

        const loginWithEmail = async () => {
            try {
                error.value = '';
                success.value = '';
                
                // Use native authentication
                const result = await nativeAuthService.login(email.value, password.value);
                
                if (result.success) {
                    router.push('/dashboard');
                } else {
                    error.value = result.error || 'Login failed. Please check your credentials.';
                }
            } catch (err) {
                console.error('Email login error:', err);
                error.value = 'Login failed. Please check your credentials.';
            }
        };

        const toggleForgotPassword = () => {
            showForgotPassword.value = !showForgotPassword.value;
            error.value = '';
            success.value = '';
        };

        return {
            error,
            success,
            email,
            password,
            showForgotPassword,
            showEmailLogin,
            showPassword,
            loginWithEmail,
            forgotPassword,
            toggleForgotPassword
        };
    }
};
</script>

<style scoped>
.login-container {
    max-width: 600px;
    margin: 40px auto;
    padding: 40px 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 80vh;
    justify-content: center;
}

.logo-section {
    margin-bottom: 30px;
    width: 100%;
    height: auto;
    display: flex;
    justify-content: center;
    align-items: center;
}

.main-logo {
    width: 200px;
    height: auto;
    margin-bottom: 20px;
}

.welcome-text {
    font-size: 2.5rem;
    color: #2c3e50;
    margin-bottom: 10px;
    font-weight: 600;
}

.members-text {
    font-size: 1.8rem;
    color: #486581;
    margin-bottom: 40px;
    font-weight: 400;
}

.login-buttons {
    margin-top: 30px;
    width: 100%;
    max-width: 300px;
}

.google-signin-button {
    width: 240px;
    height: auto;
    cursor: pointer;
    transition: transform 0.2s ease;
}

.google-signin-button:hover {
    transform: scale(1.02);
}

.error-container {
    margin-top: 20px;
    padding: 12px;
    background-color: #fee2e2;
    border: 1px solid #ef4444;
    border-radius: 4px;
    width: 100%;
    max-width: 400px;
}

.error-message {
    color: #dc2626;
    margin: 0;
    text-align: center;
    font-size: 0.95rem;
}

@media (max-width: 768px) {
    .login-container {
        padding: 20px;
    }

    .welcome-text {
        font-size: 2rem;
    }

    .members-text {
        font-size: 1.5rem;
    }
}

.login-form {
    max-width: 400px;
    margin: 0 auto 20px auto;
    padding: 20px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.form-group {
    margin-bottom: 15px;
}

.form-group label {
    display: block;
    margin-bottom: 5px;
    color: #2c3e50;
}

.form-group input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.password-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.password-input {
    flex: 1;
    padding-right: 40px;
}

.password-toggle {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
    transition: opacity 0.2s;
}

.password-toggle:hover {
    opacity: 1;
}

.login-btn {
    width: 100%;
    padding: 10px;
    background: #4285f4;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
}

.login-btn:hover {
    background: #357abd;
}

.forgot-password-text {
    text-align: center;
    margin-top: 15px;
    color: #666;
}

.forgot-password-text a {
    color: #4285f4;
    text-decoration: none;
}

.forgot-password-text a:hover {
    text-decoration: underline;
}

.success-container {
    margin-top: 20px;
    padding: 12px;
    background-color: #dcfce7;
    border: 1px solid #22c55e;
    border-radius: 4px;
    width: 100%;
    max-width: 400px;
}

.success-message {
    color: #15803d;
    margin: 0;
    text-align: center;
    font-size: 0.95rem;
}
</style>